import React, { useEffect, useState } from 'react';
import { getPerfiles, crearPerfil, editarPerfil, borrarPerfil } from '../services/api';
import UsuariosTabs from '../components/UsuariosTabs';

const permisosVacios = (modulos) => Object.fromEntries(modulos.map(m => [m.key, { ver: false, editar: false }]));

export default function Perfiles() {
  const [modulos, setModulos] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [ed, setEd] = useState(null);   // perfil en edición/creación
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => getPerfiles().then(r => { setModulos(r.data.modulos); setPerfiles(r.data.perfiles); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };

  const nuevo = () => setEd({ nombre: '', descripcion: '', permisos: permisosVacios(modulos), es_sistema: false, bloqueado: false });
  const editar = (p) => setEd({
    id: p.id, nombre: p.nombre, descripcion: p.descripcion || '',
    permisos: { ...permisosVacios(modulos), ...p.permisos },
    es_sistema: p.es_sistema, bloqueado: p.rol_legacy === 'administrador',
  });

  const toggle = (mod, nivel) => {
    if (ed.bloqueado) return;
    const cur = ed.permisos[mod] || { ver: false, editar: false };
    let next;
    if (nivel === 'editar') next = { ver: !cur.editar ? true : cur.ver, editar: !cur.editar };
    else next = { ver: !cur.ver, editar: !cur.ver ? cur.editar : false };
    if (nivel === 'ver' && !next.ver) next.editar = false; // quitar ver quita editar
    setEd({ ...ed, permisos: { ...ed.permisos, [mod]: next } });
  };

  const guardar = async () => {
    if (!ed.nombre.trim()) return flash(false, 'El nombre es requerido');
    setBusy(true);
    try {
      const payload = { nombre: ed.nombre, descripcion: ed.descripcion, permisos: ed.permisos };
      if (ed.id) await editarPerfil(ed.id, payload); else await crearPerfil(payload);
      setEd(null); load(); flash(true, '✅ Perfil guardado');
    } catch (err) { flash(false, '❌ ' + (err.response?.data?.error || 'No se pudo guardar')); }
    finally { setBusy(false); }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el perfil "${p.nombre}"?`)) return;
    try { await borrarPerfil(p.id); load(); flash(true, 'Perfil eliminado'); }
    catch (err) { flash(false, '❌ ' + (err.response?.data?.error || 'No se pudo eliminar')); }
  };

  const nMods = (p) => modulos.filter(m => p.permisos?.[m.key]?.ver || p.permisos?.[m.key]?.editar).length;

  return (
    <div style={s.wrap}>
      <UsuariosTabs />
      <div style={s.head}>
        <div>
          <h1 style={s.h1}>Perfiles</h1>
          <p style={s.sub}>Define qué módulos ve y edita cada tipo de usuario.</p>
        </div>
        <button style={s.btn} onClick={nuevo}>+ Nuevo perfil</button>
      </div>

      {msg && <div style={{ ...s.msg, background: msg.ok ? '#064e3b' : '#450a0a', color: msg.ok ? '#6ee7b7' : '#fca5a5' }}>{msg.text}</div>}

      <div style={s.grid}>
        {perfiles.map(p => (
          <div key={p.id} style={s.pcard}>
            <div style={s.pcardHead}>
              <div>
                <div style={s.pname}>{p.nombre} {p.es_sistema && <span style={s.sysTag}>sistema</span>}</div>
                <div style={s.pdesc}>{p.descripcion || '—'}</div>
              </div>
            </div>
            <div style={s.pmeta}>{nMods(p)} módulo(s) · {p.usuarios} usuario(s)</div>
            <div style={s.pcardBtns}>
              <button style={s.link} onClick={() => editar(p)}>{p.rol_legacy === 'administrador' ? 'Ver' : 'Editar'}</button>
              {!p.es_sistema && <button style={{ ...s.link, color: '#fca5a5' }} onClick={() => eliminar(p)}>Eliminar</button>}
            </div>
          </div>
        ))}
      </div>

      {ed && (
        <div style={s.overlay} onClick={() => setEd(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{ed.id ? (ed.bloqueado ? 'Perfil Administrador' : 'Editar perfil') : 'Nuevo perfil'}</h3>
            {ed.bloqueado && <div style={s.info}>El perfil Administrador siempre tiene acceso total y no se modifica.</div>}

            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={s.label}>Nombre</label>
                <input style={{ ...s.input, ...((ed.es_sistema || ed.bloqueado) ? s.inputRO : {}) }}
                  value={ed.nombre} disabled={ed.es_sistema || ed.bloqueado}
                  onChange={e => setEd({ ...ed, nombre: e.target.value })} />
              </div>
              <div style={{ flex: '2 1 240px' }}>
                <label style={s.label}>Descripción</label>
                <input style={{ ...s.input, ...(ed.bloqueado ? s.inputRO : {}) }}
                  value={ed.descripcion} disabled={ed.bloqueado}
                  onChange={e => setEd({ ...ed, descripcion: e.target.value })} />
              </div>
            </div>

            <div style={s.matrixWrap}>
              <table style={s.matrix}>
                <thead><tr>
                  <th style={{ ...s.mth, textAlign: 'left' }}>Módulo</th>
                  <th style={s.mth}>Ver</th>
                  <th style={s.mth}>Editar</th>
                </tr></thead>
                <tbody>
                  {modulos.map(m => {
                    const p = ed.permisos[m.key] || { ver: false, editar: false };
                    return (
                      <tr key={m.key}>
                        <td style={s.mtd}>{m.label}</td>
                        <td style={{ ...s.mtd, textAlign: 'center' }}>
                          <input type="checkbox" checked={!!p.ver} disabled={ed.bloqueado} onChange={() => toggle(m.key, 'ver')} />
                        </td>
                        <td style={{ ...s.mtd, textAlign: 'center' }}>
                          <input type="checkbox" checked={!!p.editar} disabled={ed.bloqueado} onChange={() => toggle(m.key, 'editar')} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={s.modalBtns}>
              <button style={s.btnGhost} onClick={() => setEd(null)}>{ed.bloqueado ? 'Cerrar' : 'Cancelar'}</button>
              {!ed.bloqueado && <button style={s.btn} disabled={busy} onClick={guardar}>{busy ? 'Guardando…' : 'Guardar'}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { padding: 4 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 },
  h1: { color: '#f1f5f9', fontSize: 24, fontWeight: 700, margin: '0 0 4px' },
  sub: { color: '#64748b', fontSize: 14, margin: 0 },
  btn: { background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer' },
  msg: { padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  pcard: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  pcardHead: { display: 'flex', justifyContent: 'space-between' },
  pname: { color: '#f1f5f9', fontSize: 15, fontWeight: 700 },
  sysTag: { fontSize: 10, color: '#93c5fd', background: '#1e40af', borderRadius: 20, padding: '1px 7px', fontWeight: 600, verticalAlign: 'middle' },
  pdesc: { color: '#64748b', fontSize: 12, marginTop: 3 },
  pmeta: { color: '#94a3b8', fontSize: 12 },
  pcardBtns: { display: 'flex', gap: 14, marginTop: 4 },
  link: { background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 13, padding: 0 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 },
  modal: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 18, width: 560, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: '0 0 14px' },
  info: { background: '#1e3a8a', color: '#bfdbfe', fontSize: 12, padding: '8px 12px', borderRadius: 8, marginBottom: 14 },
  matrixWrap: { border: '1px solid #334155', borderRadius: 8, overflow: 'hidden' },
  matrix: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  mth: { padding: '8px 12px', color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5, background: '#0f172a', textAlign: 'center' },
  mtd: { padding: '8px 12px', color: '#cbd5e1', borderTop: '1px solid #1e293b' },
  modalBtns: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 },
  label: { display: 'block', color: '#94a3b8', fontSize: 12, marginBottom: 5 },
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '9px 12px', color: '#f1f5f9', fontSize: 14 },
  inputRO: { opacity: .6, cursor: 'not-allowed' },
};
